import { useEffect, useMemo, useRef, useState } from "react";
import type { LunimarRealm } from "./lunimarRealms";
const ambientPoster = { url: "/assets/lunimar-ambient-poster.jpg" };
const ambientVideo = { url: "/assets/lunimar-ambient-720.mp4" };

interface Props {
  realm: LunimarRealm;
}

/**
 * Fixed, full-viewport immersive backdrop for a Lunimar module.
 *
 * Layers, back to front:
 *   1. Midnight base
 *   2. Two atmospheric radial gradients (primary + secondary accents)
 *   3. A faint constellation grid SVG
 *   4. Drifting "dust" particles (CSS keyframes, reduced-motion safe)
 *
 * Sits behind the shell content via `pointer-events-none` so it never
 * interferes with interactions.
 */
const AmbientWorldLayer = ({ realm }: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [videoUnavailable, setVideoUnavailable] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Deterministic particle positions per realm so SSR/CSR match and the
  // scene doesn't reshuffle on every render.
  const particles = useMemo(() => {
    const seed = realm.key.length * 7 + 3;
    return Array.from({ length: 22 }, (_, i) => {
      const x = ((i * 137 + seed * 13) % 100);
      const y = ((i * 89 + seed * 17) % 100);
      const size = 1 + ((i * 31 + seed) % 3);
      const delay = ((i * 41) % 80) / 10;
      const dur = 18 + ((i * 13) % 22);
      return { x, y, size, delay, dur };
    });
  }, [realm.key]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      style={{ contain: "strict" }}
    >
      {/* Midnight base */}
      <div
        className="absolute inset-0"
        style={{
          background: "hsl(232 60% 5%)",
        }}
      />
      {/* Always-visible Lunimar world still, so the realm reads even before video playback starts. */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${ambientPoster.url})`,
          opacity: 0.92,
        }}
      />
      {/* Ambient video underlay — optimized H.264 for reliable browser playback. */}
      {!reducedMotion && !videoUnavailable && (
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: 0.9 }}
          src={ambientVideo.url}
          poster={ambientPoster.url}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onCanPlay={() => videoRef.current?.play().catch(() => setVideoUnavailable(true))}
          onError={() => setVideoUnavailable(true)}
          aria-hidden
        />
      )}
      {/* Soft realm-accent wash over the video */}
      <div
        className="absolute inset-0 mix-blend-multiply opacity-25"
        style={{
          background: `linear-gradient(135deg, hsl(${realm.secondaryAccent}) 0%, hsl(${realm.primaryAccent}) 100%)`,
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, hsl(232 60% 5% / 0.18) 0%, hsl(232 60% 5% / 0.35) 100%)",
        }}
      />
      {/* Primary atmospheric bloom */}
      <div
        className="absolute -top-1/3 left-1/2 h-[80vh] w-[80vw] -translate-x-1/2 rounded-full opacity-15 lunimar-drift"
        style={{
          background: `radial-gradient(circle, hsl(${realm.primaryAccent} / 0.55) 0%, transparent 60%)`,
          filter: "blur(60px)",
        }}
      />
      {/* Secondary haze */}
      <div
        className="absolute -bottom-1/4 -left-1/4 h-[70vh] w-[70vw] rounded-full opacity-15 lunimar-drift-slow"
        style={{
          background: `radial-gradient(circle, hsl(${realm.secondaryAccent} / 0.5) 0%, transparent 65%)`,
          filter: "blur(80px)",
        }}
      />
      <div
        className="absolute -top-1/4 -right-1/4 h-[60vh] w-[60vw] rounded-full opacity-10"
        style={{
          background: `radial-gradient(circle, hsl(${realm.tertiaryAccent} / 0.35) 0%, transparent 70%)`,
          filter: "blur(90px)",
        }}
      />
      {/* Constellation grid */}
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="lunimar-grid" width="64" height="64" patternUnits="userSpaceOnUse">
            <path d="M 64 0 L 0 0 0 64" fill="none" stroke="white" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lunimar-grid)" />
      </svg>
      {/* Dust particles */}
      <div className="absolute inset-0">
        {particles.map((p, i) => (
          <span
            key={i}
            className="absolute rounded-full lunimar-particle"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: `hsl(${realm.primaryAccent} / 0.65)`,
              boxShadow: `0 0 ${p.size * 4}px hsl(${realm.primaryAccent} / 0.5)`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.dur}s`,
            }}
          />
        ))}
      </div>
      {/* Subtle vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 60%, hsl(230 60% 4% / 0.55) 100%)",
        }}
      />
    </div>
  );
};

export default AmbientWorldLayer;